import { useEffect, useState, useRef, useCallback } from "react";
import { createPortal } from "react-dom";
import { Badge } from "./Badge";
import { Confetti } from "./Confetti";
import { Button } from "./Button";
import { getBadgeInfo, DISCIPLINES, type DisciplineSlug } from "@jumpbean/shared";

export interface BadgeEarnedModalProps {
  /** Whether the modal is open */
  isOpen: boolean;
  /** Callback when the modal should close */
  onClose: () => void;
  /** Discipline the badge was earned in */
  discipline: DisciplineSlug;
  /** Level number (1-5) */
  level: number;
  /** Optional callback when continue button is clicked */
  onContinue?: () => void;
  /** Optional custom continue button text */
  continueText?: string;
}

/**
 * BadgeEarnedModal - A magical celebration modal displayed when a child earns a badge.
 *
 * Features:
 * - Dramatic entrance animation with the badge appearing in a burst of sparkles
 * - Confetti explosion celebration
 * - Character-themed congratulations message
 * - Playful bouncy animations throughout
 * - Large touch-friendly continue button
 *
 * @example
 * <BadgeEarnedModal
 *   isOpen={showBadgeModal}
 *   onClose={() => setShowBadgeModal(false)}
 *   discipline="math"
 *   level={1}
 *   onContinue={handleContinue}
 * />
 */
export function BadgeEarnedModal({
  isOpen,
  onClose,
  discipline,
  level,
  onContinue,
  continueText = "Keep Playing!",
}: BadgeEarnedModalProps) {
  const [animationStage, setAnimationStage] = useState<
    "hidden" | "entering" | "badge-reveal" | "text-reveal" | "complete"
  >("hidden");
  const [showConfetti, setShowConfetti] = useState(false);
  const modalRef = useRef<HTMLDivElement>(null);

  const badgeInfo = getBadgeInfo(discipline, level);
  const disciplineInfo = DISCIPLINES[discipline];

  // Animation sequence
  useEffect(() => {
    if (isOpen) {
      setAnimationStage("entering");

      const timers: ReturnType<typeof setTimeout>[] = [];

      // Stage 2: Badge reveal with confetti burst
      timers.push(
        setTimeout(() => {
          setAnimationStage("badge-reveal");
          setShowConfetti(true);
        }, 300)
      );

      // Stage 3: Text reveal
      timers.push(
        setTimeout(() => {
          setAnimationStage("text-reveal");
        }, 800)
      );

      // Stage 4: Complete
      timers.push(
        setTimeout(() => {
          setAnimationStage("complete");
        }, 1300)
      );

      return () => {
        timers.forEach(clearTimeout);
      };
    } else {
      setAnimationStage("hidden");
      setShowConfetti(false);
    }
  }, [isOpen]);

  // Focus management
  useEffect(() => {
    if (isOpen && animationStage === "complete") {
      modalRef.current?.focus();
    }
  }, [isOpen, animationStage]);

  // Handle continue click
  const handleContinue = useCallback(() => {
    onContinue?.();
    onClose();
  }, [onContinue, onClose]);

  // Handle escape key
  useEffect(() => {
    if (!isOpen || animationStage !== "complete") return;

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        onClose();
      }
    };

    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [isOpen, animationStage, onClose]);

  if (!isOpen && animationStage === "hidden") return null;

  // Discipline-specific colors
  const disciplineColors: Record<DisciplineSlug, {
    gradient: string;
    glow: string;
    accent: string;
  }> = {
    math: {
      gradient: "from-luna-light via-luna to-magic-400",
      glow: "shadow-[0_0_60px_rgba(232,160,255,0.6)]",
      accent: "text-magic-600",
    },
    spelling: {
      gradient: "from-petal-light via-petal to-meadow-400",
      glow: "shadow-[0_0_60px_rgba(144,238,144,0.6)]",
      accent: "text-meadow-600",
    },
    patterns: {
      gradient: "from-prism-red via-prism-yellow to-prism-blue",
      glow: "shadow-[0_0_60px_rgba(255,230,109,0.6)]",
      accent: "text-prism-purple",
    },
    typing: {
      gradient: "from-whiskers-light via-whiskers to-sky-600",
      glow: "shadow-[0_0_60px_rgba(74,144,217,0.6)]",
      accent: "text-sky-600",
    },
    programming: {
      gradient: "from-pixel-light via-pixel to-pixel-darker",
      glow: "shadow-[0_0_60px_rgba(255,140,66,0.6)]",
      accent: "text-pixel-dark",
    },
  };

  const colors = disciplineColors[discipline];

  // Celebratory messages
  const celebrationMessages = [
    "Amazing job!",
    "You're a star!",
    "Fantastic work!",
    "Way to go!",
    "Super awesome!",
  ];
  const randomMessage = celebrationMessages[level % celebrationMessages.length];

  const modalContent = (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center"
      role="presentation"
    >
      {/* Confetti celebration */}
      <Confetti
        isActive={showConfetti}
        count={80}
        duration={4000}
        onComplete={() => setShowConfetti(false)}
      />

      {/* Backdrop with animated stars */}
      <div
        className={`
          absolute inset-0
          bg-gradient-to-b from-night/80 via-night/90 to-night
          transition-opacity duration-500
          ${animationStage === "hidden" ? "opacity-0" : "opacity-100"}
        `}
        onClick={animationStage === "complete" ? onClose : undefined}
      >
        {/* Floating sparkles in backdrop */}
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          {Array.from({ length: 20 }).map((_, i) => (
            <div
              key={i}
              className="absolute animate-float"
              style={{
                left: `${Math.random() * 100}%`,
                top: `${Math.random() * 100}%`,
                animationDelay: `${Math.random() * 3}s`,
                animationDuration: `${3 + Math.random() * 2}s`,
              }}
            >
              <SparkleIcon
                className="w-3 h-3 text-sunshine-400/40"
                style={{ transform: `rotate(${Math.random() * 360}deg)` }}
              />
            </div>
          ))}
        </div>
      </div>

      {/* Modal content */}
      <div
        ref={modalRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby="badge-earned-title"
        tabIndex={-1}
        className={`
          relative
          w-full max-w-sm mx-4
          flex flex-col items-center
          focus:outline-none
        `}
      >
        {/* Celebration title - above badge */}
        <div
          className={`
            mb-6 text-center
            transition-all duration-500
            ${
              animationStage === "hidden" || animationStage === "entering"
                ? "opacity-0 transform -translate-y-4"
                : "opacity-100 transform translate-y-0"
            }
          `}
        >
          <h2
            id="badge-earned-title"
            className="font-display font-extrabold text-display-lg text-sunshine-400 drop-shadow-[0_2px_4px_rgba(0,0,0,0.3)]"
          >
            Badge Earned!
          </h2>
        </div>

        {/* Badge with glow effect */}
        <div
          className={`
            relative mb-8
            transition-all duration-700
            ${
              animationStage === "hidden" || animationStage === "entering"
                ? "opacity-0 transform scale-0"
                : "opacity-100 transform scale-100"
            }
          `}
          style={{
            transitionTimingFunction: "cubic-bezier(0.34, 1.56, 0.64, 1)",
          }}
        >
          {/* Radiating glow rings */}
          <div
            className={`
              absolute inset-0 -m-8
              rounded-full
              bg-gradient-to-r ${colors.gradient}
              opacity-30
              blur-2xl
              animate-pulse
            `}
          />
          <div
            className={`
              absolute inset-0 -m-4
              rounded-full
              bg-gradient-to-r ${colors.gradient}
              opacity-50
              blur-xl
            `}
          />

          {/* Badge with celebration animation */}
          <div className={animationStage === "badge-reveal" ? "animate-celebrate" : ""}>
            <Badge
              discipline={discipline}
              level={level}
              isEarned
              size="lg"
              animate={animationStage === "badge-reveal"}
              className={colors.glow}
            />
          </div>

          {/* Orbiting sparkles around badge */}
          {animationStage !== "hidden" && animationStage !== "entering" && (
            <div className="absolute inset-0 pointer-events-none">
              {[0, 72, 144, 216, 288].map((angle, i) => (
                <div
                  key={angle}
                  className="absolute w-full h-full"
                  style={{
                    animation: `spin ${8 + i * 0.5}s linear infinite`,
                    animationDelay: `${i * 0.2}s`,
                  }}
                >
                  <SparkleIcon
                    className="absolute -top-4 left-1/2 -translate-x-1/2 w-4 h-4 text-sunshine-300"
                    style={{ transform: `rotate(${angle}deg)` }}
                  />
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Badge name and description */}
        <div
          className={`
            text-center mb-8
            transition-all duration-500 delay-100
            ${
              animationStage === "text-reveal" || animationStage === "complete"
                ? "opacity-100 transform translate-y-0"
                : "opacity-0 transform translate-y-4"
            }
          `}
        >
          <h3 className={`font-display font-bold text-display-sm ${colors.accent} mb-2`}>
            {badgeInfo?.name ?? `Level ${level}`}
          </h3>
          <p className="font-body text-body-lg text-neutral-200 mb-3">
            {badgeInfo?.description ?? "You completed all the challenges!"}
          </p>
          <p className="font-body text-body-md text-neutral-400">
            {disciplineInfo.name} with {disciplineInfo.characterName}
          </p>
        </div>

        {/* Celebration message */}
        <div
          className={`
            mb-8
            transition-all duration-500 delay-200
            ${
              animationStage === "complete"
                ? "opacity-100 transform scale-100"
                : "opacity-0 transform scale-90"
            }
          `}
        >
          <p className="font-display font-bold text-body-xl text-sunshine-300">
            {randomMessage}
          </p>
        </div>

        {/* Continue button */}
        <div
          className={`
            w-full max-w-xs
            transition-all duration-500 delay-300
            ${
              animationStage === "complete"
                ? "opacity-100 transform translate-y-0"
                : "opacity-0 transform translate-y-4"
            }
          `}
        >
          <Button
            variant="primary"
            size="lg"
            onClick={handleContinue}
            className="w-full"
            autoFocus={animationStage === "complete"}
          >
            {continueText}
          </Button>
        </div>
      </div>
    </div>
  );

  return createPortal(modalContent, document.body);
}

// Sparkle icon component
function SparkleIcon({
  className,
  style,
}: {
  className?: string;
  style?: React.CSSProperties;
}) {
  return (
    <svg
      className={className}
      style={style}
      viewBox="0 0 24 24"
      fill="currentColor"
    >
      <path d="M12 0L14.59 8.41L23 11L14.59 13.59L12 22L9.41 13.59L1 11L9.41 8.41L12 0Z" />
    </svg>
  );
}

// Add spin keyframe if not already present
if (typeof document !== "undefined") {
  const styleId = "jumpbean-badge-modal-animations";
  if (!document.getElementById(styleId)) {
    const style = document.createElement("style");
    style.id = styleId;
    style.textContent = `
      @keyframes spin {
        from { transform: rotate(0deg); }
        to { transform: rotate(360deg); }
      }
    `;
    document.head.appendChild(style);
  }
}

export default BadgeEarnedModal;
